import { Logger } from '../utils/logger';
import { gzipSync } from 'fflate';

export interface CaptureEvent {
    event: string;
    distinct_id: string;
    properties: Record<string, unknown>;
    timestamp: string;
}

export interface BatchPayload {
    token: string;
    batch: CaptureEvent[];
}

export interface SnapshotPayload {
    token: string;
    distinct_id: string;
    batch: Array<{
        event: '$snapshot';
        properties: {
            $session_id: string;
            $window_id: string;
            $snapshot_items: unknown[];
            $snapshot_source: string;
        };
    }>;
}

// sendBeacon has a ~64KB limit, use 60KB to be safe
const SEND_BEACON_MAX_SIZE = 60 * 1024;

export class Transport {
    private apiHost: string;
    private token: string;
    private logger: Logger;
    private fetchFn: typeof fetch;
    private compression: boolean;

    constructor(
        apiHost: string,
        token: string,
        logger: Logger,
        fetchFn?: typeof fetch,
        compression: boolean = true
    ) {
        this.apiHost = apiHost.replace(/\/$/, '');
        this.token = token;
        this.logger = logger;
        this.fetchFn = fetchFn || (typeof fetch !== 'undefined' ? (url, init) => fetch(url, init) : this.noopFetch);
        this.compression = compression;
    }

    /**
     * Compress a JSON string using gzip. Returns a Blob with the compressed data,
     * or null if compression fails or isn't worth it.
     */
    private compressPayload(json: string): Blob | null {
        if (!this.compression) return null;

        try {
            const encoder = new TextEncoder();
            const data = encoder.encode(json);
            const compressed = gzipSync(data, { level: 6 });

            this.logger.debug('Compressed payload', {
                original: data.byteLength,
                compressed: compressed.byteLength,
                ratio: ((1 - compressed.byteLength / data.byteLength) * 100).toFixed(1) + '%',
            });

            // Only use compression if it actually saves space
            if (compressed.byteLength >= data.byteLength) {
                return null;
            }

            return new Blob([new Uint8Array(compressed).buffer], { type: 'application/json' });
        } catch (e) {
            this.logger.warn('Compression failed, sending uncompressed', { error: e });
            return null;
        }
    }

    async sendBatch(events: CaptureEvent[]): Promise<void> {
        const payload: BatchPayload = {
            token: this.token,
            batch: events,
        };

        const url = `${this.apiHost}/capture/batch`;
        const json = JSON.stringify(payload);
        const compressed = this.compressPayload(json);

        this.logger.debug('Sending batch', { url, count: events.length });

        const response = await this.fetchFn(url, {
            method: 'POST',
            headers: compressed
                ? { 'Content-Type': 'application/json', 'Content-Encoding': 'gzip-js' }
                : { 'Content-Type': 'application/json' },
            body: compressed || json,
        });

        if (!response.ok) {
            const text = await response.text().catch(() => '');
            throw new Error(`HTTP ${response.status}: ${text}`);
        }

        const result = await response.json();
        if (result.status !== 1) {
            throw new Error(`Capture failed: ${JSON.stringify(result)}`);
        }
    }

    sendBeacon(events: CaptureEvent[]): boolean {
        const payload: BatchPayload = {
            token: this.token,
            batch: events,
        };

        const json = JSON.stringify(payload);
        const compressed = this.compressPayload(json);
        const url = compressed
            ? `${this.apiHost}/capture/batch?compression=gzip-js`
            : `${this.apiHost}/capture/batch`;
        const body = compressed || json;
        const contentType = 'application/json';

        // Try sendBeacon first (works during page unload)
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
            const blob = compressed || new Blob([json], { type: contentType });
            const success = navigator.sendBeacon(url, blob);
            if (success) {
                this.logger.debug('sendBeacon succeeded', { count: events.length });
                return true;
            }
            this.logger.warn('sendBeacon failed (browser limit?), falling back to fetch');
        }

        // Fallback: fire-and-forget fetch with keepalive (survives page unload)
        try {
            this.fetchFn(url, {
                method: 'POST',
                headers: compressed
                    ? { 'Content-Type': contentType, 'Content-Encoding': 'gzip-js' }
                    : { 'Content-Type': contentType },
                body,
                keepalive: true,
            }).catch(() => {
                // Best-effort — page may be unloading
            });
            return true;
        } catch {
            this.logger.warn('fetch keepalive fallback also failed');
            return false;
        }
    }

    async sendRecording(
        distinctId: string,
        sessionId: string,
        windowId: string,
        events: unknown[]
    ): Promise<void> {
        const payload: SnapshotPayload = {
            token: this.token,
            distinct_id: distinctId,
            batch: [
                {
                    event: '$snapshot',
                    properties: {
                        $session_id: sessionId,
                        $window_id: windowId,
                        $snapshot_items: events,
                        $snapshot_source: 'web',
                    },
                },
            ],
        };

        const json = JSON.stringify(payload);
        const compressed = this.compressPayload(json);
        const url = `${this.apiHost}/s/`;

        this.logger.debug('Sending recording', { url, eventCount: events.length });

        const response = await this.fetchFn(url, {
            method: 'POST',
            headers: compressed
                ? { 'Content-Type': 'application/json', 'Content-Encoding': 'gzip-js' }
                : { 'Content-Type': 'application/json' },
            body: compressed || json,
        });

        if (!response.ok) {
            const text = await response.text().catch(() => '');
            throw new Error(`HTTP ${response.status}: ${text}`);
        }

        const result = await response.json().catch(() => null);
        if (result && result.status !== undefined && result.status !== 1) {
            throw new Error(`Recording capture failed: ${result.error || JSON.stringify(result)}`);
        }
    }

    sendRecordingBeacon(
        distinctId: string,
        sessionId: string,
        windowId: string,
        events: unknown[]
    ): boolean {
        const baseUrl = `${this.apiHost}/s/`;

        const buildPayload = (eventBatch: unknown[]): SnapshotPayload => ({
            token: this.token,
            distinct_id: distinctId,
            batch: [
                {
                    event: '$snapshot',
                    properties: {
                        $session_id: sessionId,
                        $window_id: windowId,
                        $snapshot_items: eventBatch,
                        $snapshot_source: 'web',
                    },
                },
            ],
        });

        /**
         * Send a chunk via sendBeacon, falling back to fetch(keepalive) on failure.
         * Tries gzip compression first, then falls back to uncompressed.
         */
        const sendChunk = (eventBatch: unknown[]): boolean => {
            const json = JSON.stringify(buildPayload(eventBatch));
            const compressed = this.compressPayload(json);

            // Determine URL and body based on compression
            const url = compressed ? `${baseUrl}?compression=gzip-js` : baseUrl;
            const body = compressed || json;

            // Try sendBeacon first
            if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
                const blob = compressed || new Blob([json], { type: 'application/json' });
                if (navigator.sendBeacon(url, blob)) {
                    return true;
                }
                this.logger.warn('sendBeacon chunk failed, falling back to fetch', { chunkSize: eventBatch.length });
            }

            // Fallback: fire-and-forget fetch with keepalive
            try {
                this.fetchFn(url, {
                    method: 'POST',
                    headers: compressed
                        ? { 'Content-Type': 'application/json', 'Content-Encoding': 'gzip-js' }
                        : { 'Content-Type': 'application/json' },
                    body,
                    keepalive: true,
                }).catch(() => {
                    // Best-effort during unload
                });
                return true;
            } catch {
                return false;
            }
        };

        const fullJson = JSON.stringify(buildPayload(events));
        const fullCompressed = this.compressPayload(fullJson);

        // Check if the (possibly compressed) payload fits in one sendBeacon call
        const effectiveSize = fullCompressed ? fullCompressed.size : fullJson.length;

        if (effectiveSize <= SEND_BEACON_MAX_SIZE) {
            const url = fullCompressed ? `${baseUrl}?compression=gzip-js` : baseUrl;

            if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
                const blob = fullCompressed || new Blob([fullJson], { type: 'application/json' });
                if (navigator.sendBeacon(url, blob)) return true;
            }
            // Fallback
            return sendChunk(events);
        }

        // Still too large after compression — need to chunk
        this.logger.debug('Payload too large for single sendBeacon even after compression, chunking', {
            originalSize: fullJson.length,
            compressedSize: fullCompressed ? fullCompressed.size : fullJson.length,
            eventCount: events.length,
        });

        const overheadSize = JSON.stringify(buildPayload([])).length;
        // Use a conservative estimate — compression typically achieves ~85% reduction
        // but we size chunks against uncompressed to ensure they compress under the limit
        const availableSize = SEND_BEACON_MAX_SIZE * 4 - overheadSize - 100;

        // Build atomic groups: Meta (type 4) + FullSnapshot (type 2) stay together
        const groups: unknown[][] = [];
        let i = 0;
        while (i < events.length) {
            const event = events[i] as { type?: number };

            // If this is a Meta event and next is a FullSnapshot, group them
            if (event.type === 4 && i + 1 < events.length) {
                const next = events[i + 1] as { type?: number };
                if (next.type === 2) {
                    groups.push([events[i], events[i + 1]]);
                    i += 2;
                    continue;
                }
            }

            groups.push([events[i]]);
            i++;
        }

        // Chunk groups respecting size limits
        let currentChunk: unknown[] = [];
        let currentSize = 0;
        let allSuccess = true;

        for (const group of groups) {
            const groupJson = JSON.stringify(group);
            const groupSize = groupJson.length;

            if (currentSize + groupSize > availableSize && currentChunk.length > 0) {
                if (!sendChunk(currentChunk)) allSuccess = false;
                currentChunk = [];
                currentSize = 0;
            }

            currentChunk.push(...group);
            currentSize += groupSize;
        }

        if (currentChunk.length > 0) {
            if (!sendChunk(currentChunk)) allSuccess = false;
        }

        return allSuccess;
    }

    private noopFetch(): Promise<Response> {
        return Promise.resolve(new Response('{"status":1}', { status: 200 }));
    }
}
